import { useRef, useState, useEffect } from 'react';
import MembershipLine from './MembershipLine';
import { MembershipCard } from './MembershipCard';

export default function Membership() {
  const scrollRef = useRef(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Adjust breakpoint as needed (768px for md)
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const cardData = [
    {
      icon: '/card1.png',
      title: 'Connect your XRP Wallet',
      subtitle: 'Securely connect your wallet to begin and verify your identity on the XRP Ledger.',
    },
    {
      icon: '/card2.png',
      title: 'Link a Social ID (Optional)',
      subtitle: 'You can link your Google, Apple, or Meta account — or skip this step entirely.',
    },
    {
      icon: '/card3.png',
      title: 'Review the Operating Agreement',
      subtitle: 'Read through XAO DAO’s legal and structural framework before joining.',
    },
    {
      icon: '/card4.png',
      title: 'Sign On-Chain (EVM Sidechain)',
      subtitle: 'Cryptographically sign that you’ve reviewed the agreement — trustless and binding.',
    },
    {
      icon: '/card5.png',
      title: 'Send Membership Payment',
      subtitle: 'Transfer the membership fee to the XAO DAO, LLC wallet with the provided tag.',
    },
    {
      icon: '/card6.png',
      title: 'Confirm Payment & Mint NFT',
      subtitle: 'Once confirmed, your membership NFT is minted and deposited into your XRP wallet.',
    },
  ];

  const handleCardSelect = (index, isSelected) => {
    setSelectedCardIndex(isSelected ? index : null);
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <section
      className="w-screen min-h-screen flex flex-col items-center justify-center relative text-black py-8 md:py-0"
      style={{
        // backgroundImage: `url("/membershipBg.png")`,
        // backgroundSize: 'cover',
        // backgroundPosition: 'center',
        // backgroundRepeat: 'no-repeat',
        background: "#edebeb"
      }}
    >
      <div className="w-full flex flex-col items-center justify-center mt-8 z-10">
        <p className="w-11/12 md:w-[976px] text-center font-inter font-semibold text-sm md:text-[16px] leading-[1.6] uppercase tracking-wider">
          Powered by the Ledger.
        </p>
        <p className="w-11/12 md:w-[976px] text-center mt-2 text-2xl md:text-4xl font-medium">
          Membership On Your Terms.
        </p>
        <div className="w-11/12 md:w-[922px] text-center  font-inter font-normal text-sm md:text-[16px] leading-[2.2] mt-4">
          Membership in XAO DAO is open to anyone who shares our vision of transparent, community-led governance for the XRPL ecosystem. Joining is simple: register and connect your wallet to become a recognized member. Membership allows you to take part in decision-making, submit proposals, and vote on how resources are managed.
        </div>
        <div className="w-11/12 md:w-[70%] mx-auto flex items-start justify-start gap-4 mt-8">
          {isMobile ? (
            <div className="relative w-full flex flex-col items-center">
              <div
                className="absolute top-0 left-1/2 h-full w-px -translate-x-1/2 z-0"
                style={{
                  background: '#ffffff31',
                  backgroundSize: '1px 10px',
                  backgroundRepeat: 'repeat-y',
                }}
              ></div>
              <div className="flex flex-col items-center gap-8">
                {cardData.map((card, index) => (
                  <div key={index} className="flex flex-col items-center gap-4 w-full">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold text-sm z-10"
                      style={{
                        background: selectedCardIndex === index ? 'linear-gradient(90deg, #00E1FF, #7F4FFF)' : 'transparent',
                        border: selectedCardIndex !== index ? '1px solid #47556950' : 'none',
                      }}
                    >
                      {index + 1}
                    </div>
                    <MembershipCard
                      icon={card.icon}
                      title={card.title}
                      subtitle={card.subtitle}
                      isSelected={selectedCardIndex === index}
                      onSelect={(isSelected) => handleCardSelect(index, isSelected)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="flex-1 flex flex-col gap-4 min-w-0 h-full p-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollBehavior: 'smooth', overflowX: 'auto' }}
            >
              <MembershipLine cardCount={cardData.length} selectedCardIndex={selectedCardIndex} />
              <div className="flex flex-row items-center gap-4">
                <div className="flex flex-row gap-4">
                  {cardData.map((card, index) => (
                    <MembershipCard
                      key={index}
                      icon={card.icon}
                      title={card.title}
                      subtitle={card.subtitle}
                      className="min-w-[350px]"
                      isSelected={selectedCardIndex === index}
                      onSelect={(isSelected) => handleCardSelect(index, isSelected)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <button
            className={`flex items-center justify-center bg-black/40 rounded-full w-8 h-8 hover:bg-black/60 transition-colors flex-shrink-0 mt-48 ${isMobile ? 'hidden' : ''}`}
            aria-label="Scroll cards right"
            onClick={handleScrollRight}
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 6L16 12L10 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className="w-11/12 md:w-[70%] mx-auto flex justify-center mt-4 md:mt-8 mb-8 md:mb-20">
          <button
            className="px-6 md:px-8 py-2 md:py-3 text-white font-semibold rounded-full shadow-lg text-sm md:text-base transition-colors duration-200"
            style={{
              background: 'linear-gradient(90deg, #00E1FF 0%, #7F4FFF 100%)',
            }}
            type="button"
          >
            <a href={process.env.REACT_APP_XAODAO_URL} target="_blank" rel="noopener noreferrer">
              Connect your XRP Wallet
            </a>
          </button>
        </div>
      </div>
    </section>
  );
}